"use client";
import {
  createParser,
  parseAsArrayOf,
  parseAsBoolean,
  parseAsFloat,
  parseAsHex,
  parseAsInteger,
  parseAsIsoDateTime,
  parseAsJson,
  parseAsString,
  parseAsStringEnum,
  parseAsTimestamp
} from "./chunk-UOIJVQM4.js";

// src/lib/deprecated.ts
var queryTypes = {
  /**
   * @deprecated use `parseAsString` instead.
   */
  string: parseAsString,
  /**
   * @deprecated use `parseAsInteger` instead.
   */
  integer: parseAsInteger,
  /**
   * @deprecated use `parseAsFloat` instead.
   */
  float: parseAsFloat,
  /**
   * @deprecated use `parseAsBoolean` instead.
   */
  boolean: parseAsBoolean,
  /**
   * @deprecated use `parseAsTimestamp` instead.
   */
  timestamp: parseAsTimestamp,
  /**
   * @deprecated use `parseAsIsoDateTime` instead.
   */
  isoDateTime: parseAsIsoDateTime,
  /**
   * @deprecated use `parseAsStringEnum` instead.
   */
  stringEnum: parseAsStringEnum,
  /**
   * @deprecated use `parseAsJson` instead.
   */
  json: parseAsJson,
  /**
   * @deprecated use `parseAsArrayOf` instead.
   */
  array: parseAsArrayOf
};

// src/lib/sync.ts
import Mitt from "mitt";
var SYNC_EVENT_KEY = Symbol("__nextUseQueryState__SYNC__");
var NOSYNC_MARKER = "__nextUseQueryState__NO_SYNC__";
var NOTIFY_EVENT_KEY = Symbol("__nextUseQueryState__NOTIFY__");
var emitter = Mitt();
function subscribeToQueryUpdates(callback) {
  emitter.on(NOTIFY_EVENT_KEY, callback);
  return () => emitter.off(NOTIFY_EVENT_KEY, callback);
}
var patched = false;
if (!patched && typeof window === "object") {
  for (const method of ["pushState", "replaceState"]) {
    const original = window.history[method].bind(window.history);
    window.history[method] = function nextUseQueryState_patchedHistory(state, title, url) {
      if (!url) {
        return original(state, title, url);
      }
      const source = title === NOSYNC_MARKER ? "internal" : "external";
      const search = new URL(url, location.origin).searchParams;
      if (source === "external") {
        setTimeout(() => {
          emitter.emit(SYNC_EVENT_KEY, search);
          emitter.emit(NOTIFY_EVENT_KEY, { search, source });
        }, 0);
      } else {
        setTimeout(() => {
          emitter.emit(NOTIFY_EVENT_KEY, { search, source });
        }, 0);
      }
      return original(state, title === NOSYNC_MARKER ? "" : title, url);
    };
  }
  patched = true;
}

// src/lib/useQueryState.ts
import { useRouter, useSearchParams } from "next/navigation.js";
import React from "react";

// src/lib/update-queue.ts
var FLUSH_RATE_LIMIT_MS = 50;
var updateQueue = /* @__PURE__ */ new Map();
var queueOptions = {
  history: "replace",
  scroll: false,
  shallow: true
};
var lastFlushTimestamp = 0;
var flushPromiseCache = null;
function enqueueQueryStringUpdate(key, value, serialize, options) {
  updateQueue.set(key, value === null ? null : serialize(value));
  if (options.history === "push") {
    queueOptions.history = "push";
  }
  if (options.scroll) {
    queueOptions.scroll = true;
  }
  if (options.shallow === false) {
    queueOptions.shallow = false;
  }
}
function getInitialStateFromQueue(key) {
  return updateQueue.get(key) ?? null;
}
function flushToURL(router) {
  if (flushPromiseCache === null) {
    flushPromiseCache = new Promise((resolve, reject) => {
      const now = performance.now();
      const timeSinceLastFlush = now - lastFlushTimestamp;
      const flushInMs = Math.max(
        0,
        Math.min(FLUSH_RATE_LIMIT_MS, FLUSH_RATE_LIMIT_MS - timeSinceLastFlush)
      );
      setTimeout(() => {
        lastFlushTimestamp = performance.now();
        const search = flushUpdateQueue(router);
        if (!search) {
          reject();
        } else {
          resolve(search);
        }
        flushPromiseCache = null;
      }, flushInMs);
    });
  }
  return flushPromiseCache;
}
function flushUpdateQueue(router) {
  const search = new URLSearchParams(window.location.search);
  if (updateQueue.size === 0) {
    return search;
  }
  const items = Array.from(updateQueue.entries());
  const options = { ...queueOptions };
  queueOptions.history = "replace";
  queueOptions.scroll = false;
  queueOptions.shallow = true;
  updateQueue.clear();
  for (const [key, value] of items) {
    if (value === null) {
      search.delete(key);
    } else {
      search.set(key, value);
    }
  }
  const query = search.toString();
  const path = window.location.pathname;
  const hash = window.location.hash;
  const url = query ? `?${query}${hash}` : `${path}${hash}`;
  try {
    if (options.shallow) {
      const updateUrl = options.history === "push" ? window.history.pushState : window.history.replaceState;
      updateUrl.call(
        window.history,
        window.history.state,
        // Our own updates have a marker to prevent syncing
        // when the URL changes (we've already sync'd them up
        // via `emitter.emit(key, newValue)` above, without
        // going through the parsers).
        NOSYNC_MARKER,
        url
      );
      if (options.scroll) {
        window.scrollTo(0, 0);
      }
    } else {
      const updateUrl = options.history === "push" ? router.push : router.replace;
      updateUrl.call(router, url, { scroll: options.scroll });
    }
    return search;
  } catch (error) {
    console.error(
      // This may fail due to rate-limiting of history methods,
      // for example Safari only allows 100 updates in a 30s window.
      `useQueryState error updating URL: ${error}`
    );
    return null;
  }
}

// src/lib/useQueryState.ts
function useQueryState(key, {
  history = "replace",
  shallow = true,
  scroll = false,
  parse = (x) => x,
  serialize = String,
  defaultValue = void 0
} = {
  history: "replace",
  scroll: false,
  shallow: true,
  parse: (x) => x,
  serialize: String,
  defaultValue: void 0
}) {
  const router = useRouter();
  const initialSearchParams = useSearchParams();
  const [internalState, setInternalState] = React.useState(() => {
    const queueValue = getInitialStateFromQueue(key);
    const urlValue = typeof window !== "object" ? (
      // SSR
      initialSearchParams?.get(key) ?? null
    ) : (
      // Components mounted after page load must use the current URL value
      new URLSearchParams(window.location.search).get(key) ?? null
    );
    const value = queueValue ?? urlValue;
    return value === null ? null : parse(value);
  });
  const stateRef = React.useRef(internalState);
  React.useInsertionEffect(() => {
    function updateInternalState(state) {
      stateRef.current = state;
      setInternalState(state);
    }
    function syncFromURL(search) {
      const value = search.get(key) ?? null;
      const state = value === null ? null : parse(value);
      updateInternalState(state);
    }
    emitter.on(SYNC_EVENT_KEY, syncFromURL);
    emitter.on(key, updateInternalState);
    return () => {
      emitter.off(SYNC_EVENT_KEY, syncFromURL);
      emitter.off(key, updateInternalState);
    };
  }, [key]);
  const update = React.useCallback(
    (stateUpdater, options = {}) => {
      const newValue = isUpdaterFunction(stateUpdater) ? stateUpdater(stateRef.current ?? defaultValue ?? null) : stateUpdater;
      emitter.emit(key, newValue);
      enqueueQueryStringUpdate(key, newValue, serialize, {
        // Call-level options take precedence over hook declaration options.
        history: options.history ?? history,
        shallow: options.shallow ?? shallow,
        scroll: options.scroll ?? scroll
      });
      return flushToURL(router);
    },
    [
      key,
      history,
      shallow,
      scroll
      //  internalState, defaultValue
    ]
  );
  return [internalState ?? defaultValue ?? null, update];
}
function isUpdaterFunction(stateUpdater) {
  return typeof stateUpdater === "function";
}

// src/lib/useQueryStates.ts
import {
  useRouter as useRouter2,
  useSearchParams as useSearchParams2
} from "next/navigation.js";
import React2 from "react";
function useQueryStates(keyMap, {
  history = "replace",
  scroll = false,
  shallow = true
} = {}) {
  const router = useRouter2();
  const initialSearchParams = useSearchParams2();
  const [internalState, setInternalState] = React2.useState(() => {
    if (typeof window !== "object") {
      return parseMap(keyMap, initialSearchParams ?? new URLSearchParams());
    }
    return parseMap(keyMap, new URLSearchParams(window.location.search));
  });
  const stateRef = React2.useRef(internalState);
  React2.useInsertionEffect(() => {
    function updateInternalState(state) {
      stateRef.current = state;
      setInternalState(state);
    }
    function syncFromURL(search) {
      const state = parseMap(keyMap, search);
      updateInternalState(state);
    }
    const handlers = Object.keys(keyMap).reduce((handlers2, key) => {
      handlers2[key] = (value) => {
        const { defaultValue } = keyMap[key];
        stateRef.current = {
          ...stateRef.current,
          [key]: value ?? defaultValue ?? null
        };
        updateInternalState(stateRef.current);
      };
      return handlers2;
    }, {});
    emitter.on(SYNC_EVENT_KEY, syncFromURL);
    for (const key of Object.keys(keyMap)) {
      emitter.on(key, handlers[key]);
    }
    return () => {
      emitter.off(SYNC_EVENT_KEY, syncFromURL);
      for (const key of Object.keys(keyMap)) {
        emitter.off(key, handlers[key]);
      }
    };
  }, [keyMap]);
  const update = React2.useCallback(
    (stateUpdater, options = {}) => {
      const newState = typeof stateUpdater === "function" ? stateUpdater(stateRef.current) : stateUpdater;
      for (const [key, value] of Object.entries(newState)) {
        const { serialize } = keyMap[key];
        emitter.emit(key, value);
        enqueueQueryStringUpdate(key, value, serialize ?? String, {
          // Call-level options take precedence over hook declaration options.
          history: options.history ?? history,
          shallow: options.shallow ?? shallow,
          scroll: options.scroll ?? scroll
        });
      }
      return flushToURL(router);
    },
    [keyMap, history, shallow, scroll]
  );
  return [internalState, update];
}
function parseMap(keyMap, searchParams) {
  return Object.keys(keyMap).reduce((obj, key) => {
    const { defaultValue, parse } = keyMap[key];
    const urlQuery = searchParams?.get(key) ?? null;
    const queueQuery = getInitialStateFromQueue(key);
    const query = queueQuery ?? urlQuery;
    const value = query === null ? null : parse(query);
    obj[key] = value ?? defaultValue ?? null;
    return obj;
  }, {});
}
export {
  createParser,
  parseAsArrayOf,
  parseAsBoolean,
  parseAsFloat,
  parseAsHex,
  parseAsInteger,
  parseAsIsoDateTime,
  parseAsJson,
  parseAsString,
  parseAsStringEnum,
  parseAsTimestamp,
  queryTypes,
  subscribeToQueryUpdates,
  useQueryState,
  useQueryStates
};
